import { api } from './authService';
import logger from '../utils/logger';

/**
 * Chunk'lı görüşme kaydı yükleme — bkz. media-service/controllers/videoChunkController.js.
 * Kayıt periyodik parçalar halinde akarken sunucuya yüklenir (bellek sınırlı kalır),
 * görüşme bitince complete() ile birleştirilir; hiç complete gelmezse (tab çökerse)
 * sunucudaki videoChunkFinalizeWorker belirli bir süre sonra kendiliğinden birleştirir.
 */

/**
 * @param {{ sessionId: string, recordingType: 'remote'|'local', fileName: string, mimeType?: string, customerIdNumber?: string }} params
 * @returns {Promise<{ uploadId: string }>}
 */
export const initUpload = async (params) => {
    try {
        const response = await api.post('/upload/video-chunk/init', params);
        return response.data;
    } catch (error) {
        logger.error('Chunk upload başlatma hatası:', error);
        throw error;
    }
};

const CHUNK_UPLOAD_MAX_ATTEMPTS = 3;
const CHUNK_UPLOAD_BACKOFF_MS = [1000, 2000];

// Sunucu chunk'ları sıkı contiguous sırayla kabul ediyor: tek bir chunk kalıcı olarak
// kaybolursa sonraki tüm chunk'lar 409 ile reddedilip görüşme kaydı tamamen kayboluyor.
// Bu yüzden GEÇİCİ hatalarda (ağ kopması, 5xx, 429) retry deneriz; 4xx (400/404/409/413 vb.)
// kalıcı hatalardır, tekrar denemek sonucu değiştirmez, hemen fırlatılır.
const isRetryableChunkUploadError = (error) => {
    const status = error?.response?.status;
    if (!status) return true; // response yok → ağ hatası
    return status >= 500 || status === 429;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * @param {string} uploadId
 * @param {number} chunkIndex
 * @param {Blob} blob
 * @returns {Promise<{ received: boolean, chunkCount: number }>}
 */
export const uploadChunk = async (uploadId, chunkIndex, blob) => {
    let lastError;
    for (let attempt = 0; attempt < CHUNK_UPLOAD_MAX_ATTEMPTS; attempt++) {
        try {
            const response = await api.post('/upload/video-chunk/chunk', blob, {
                headers: {
                    'Content-Type': 'application/octet-stream',
                    'x-upload-id': uploadId,
                    'x-chunk-index': String(chunkIndex)
                }
            });
            return response.data;
        } catch (error) {
            lastError = error;
            const isLastAttempt = attempt === CHUNK_UPLOAD_MAX_ATTEMPTS - 1;
            if (isLastAttempt || !isRetryableChunkUploadError(error)) {
                logger.error(`Chunk yükleme hatası (index ${chunkIndex}):`, error);
                throw error;
            }
            await sleep(CHUNK_UPLOAD_BACKOFF_MS[attempt]);
        }
    }
    throw lastError;
};

/**
 * @param {string} uploadId
 * @returns {Promise<{ objectKey: string, presignedUrl: string }>}
 */
export const complete = async (uploadId) => {
    try {
        const response = await api.post('/upload/video-chunk/complete', { uploadId });
        return response.data;
    } catch (error) {
        logger.error('Chunk upload tamamlama hatası:', error);
        throw error;
    }
};

const videoChunkService = { initUpload, uploadChunk, complete };
export default videoChunkService;
